import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { z } from "zod";
import { IpcRest } from "@/hooks/ipc";
import { useAuthUser } from "@/context/auth/AuthContext";
import usePagination from "@/hooks/usePagination";
import { PaginatedResponse } from "@/schemas/common";
import { buildRestParams, paginatedQueryKey } from "./restUtils";

interface UseRestPaginatedQueryOptions {
  queryKey: string;
  url: string;
  operators?: any[];
  enabled?: boolean;
  uniqueQuery?: any[];
  hasPagination?: boolean;
  extraParams?: Record<string, any>;
  itemSchema?: z.ZodType<any>;
}

export function useRestPaginatedQuery({
  queryKey,
  url,
  operators = [],
  enabled = true,
  uniqueQuery = [],
  hasPagination = true,
  extraParams = {},
  itemSchema,
}: UseRestPaginatedQueryOptions) {
  const user = useAuthUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchData = async (page: number, limit: number) => {
    const params: Record<string, any> = {
      ...extraParams,
      ...buildRestParams(operators, hasPagination, page, limit),
    };

    const data = await IpcRest.get<PaginatedResponse<any>>(
      { url, params },
      navigate
    );

    if (itemSchema && data?.data) {
      data.data = data.data.map((item: any) => itemSchema.parse(item));
    }

    return data;
  };

  // La consulta, su prefetch y el de `usePagination` usan la misma base.
  const baseKey = paginatedQueryKey({
    queryKey,
    businessId: user?.businessId,
    uniqueQuery,
    operators,
    extraParams,
  });

  const {
    currentPage,
    offset,
    itemsPerPage,
    total,
    totalPages,
    next,
    prev,
    jump,
    setTotal,
    reset,
    setItemsPerPage,
  } = usePagination({
    queries: baseKey,
    fetchFunction: () => fetchData(currentPage + 1, itemsPerPage),
    hasPagination,
  });

  const query = useQuery(
    [
      ...baseKey,
      ...(hasPagination ? [currentPage, itemsPerPage, hasPagination] : []),
    ],
    () => fetchData(currentPage, itemsPerPage),
    {
      staleTime: 60 * 1000,
      enabled: !!enabled,
      /**
       * Una consulta SIN paginar no se reintenta. Nunca.
       *
       * `hasPagination: false` se traduce a `limit=10000` (ver
       * `buildRestParams`): el catálogo entero. En un negocio grande eso es una
       * query de ~150 segundos del lado del BFF, así que el request muere por
       * timeout a los 30s — y react-query v3, con su default de **3 reintentos
       * con backoff**, manda tres más. Cada gesto de "Ver todos" que falla se
       * convierte en cuatro consultas pesadas contra una base que ya está
       * sufriendo.
       *
       * El 2026-09-02 eso tiró el ERP entero: un solo negocio generó 178
       * requests a `/products` en 21 minutos, agotó el pool del BFF y se
       * llevaron puestos 257 requests de TODOS los endpoints y de todos los
       * usuarios. El reintento no ayuda —lo que falló por tamaño va a volver a
       * fallar por tamaño— y multiplica el daño por cuatro.
       *
       * Las paginadas bajan de 3 a 1: un reintento cubre el corte de red
       * ocasional, que es para lo que sirve.
       */
      retry: hasPagination ? 1 : 0,
    }
  );

  useEffect(() => {
    if (query.isSuccess && query.data) {
      setTotal((query.data as any).total ?? 0);
    }
  }, [query.isFetching]);

  // Prefetch next page
  useEffect(() => {
    if (hasPagination && currentPage < totalPages) {
      const nextPage = currentPage + 1;
      queryClient.prefetchQuery(
        [...baseKey, nextPage, itemsPerPage, hasPagination],
        () => fetchData(nextPage, itemsPerPage),
        { staleTime: 60 * 1000, retry: 1 }
      );
    }
  }, [currentPage, totalPages, itemsPerPage]);

  /**
   * **Tiene que ser memoizado, y no es una optimización.**
   *
   * Era `(query.data as any)?.data ?? []`. Mientras la query tiene datos, ese
   * `.data` es el mismo array de react-query y la identidad se mantiene. Pero
   * apenas la query **falla o todavía no resolvió** —`query.data` es
   * `undefined`—, el `?? []` devuelve **un array nuevo en cada render**.
   *
   * Un consumidor que ponga `items` en las dependencias de un `useCallback` o
   * un `useEffect` entra ahí en un bucle infinito de renders. Pasó de verdad:
   * `useSocket` deriva `showOSNotification` de `items` y `connectWebSocket` de
   * `showOSNotification`, y su efecto depende de `connectWebSocket` y llama a
   * `setSocket`. Con el endpoint de notificaciones caído, la cadena se
   * realimentaba sola y la app entera se iba al ErrorBoundary con "Maximum
   * update depth exceeded" — o sea, la pantalla "Algo salió mal" por no poder
   * traer las notificaciones.
   *
   * Con `useMemo` la identidad sólo cambia cuando cambia `query.data`, que es
   * lo que el consumidor espera.
   */
  const items = useMemo(() => (query.data as any)?.data ?? [], [query.data]);

  return {
    query,
    items,

    currentPage,
    offset,
    itemsPerPage,
    total,
    totalPages,
    next,
    prev,
    jump,
    setTotal,
    reset,
    setItemsPerPage,
  };
}
